import { all, fork, call, takeLatest, put } from 'redux-saga/effects';
import jwtDecode from 'jwt-decode';
import { Actions, Types } from '../reducers/Auth';
import { loginAsync, updatePasswordAsync, updateProfileAsync } from 'services/requests/auth';
import { requestUser } from 'services/requests/users';
import { Alert } from 'helpers/Alert';

function* login({ payload }) {
  try {
    const response = yield call(loginAsync, payload.email, payload.password);

    yield put(Actions.setLoginData(response.data));

    yield localStorage.setItem('token', response.data.access_token);
    yield localStorage.setItem('refresh', response.data.refresh_token);
  } catch (error) {
    yield put(Actions.loginFail());
    Alert({
      type: 'error',
      title: `Error on login attempt`,
      text: 'You typed wrong credentials or you are not an administrator user.',
    });
  }
}

function* updateProfile({ payload }) {
  try {
    yield call(loginAsync, payload.email, payload.currentPassword);

    if (payload.newPassword) yield call(updatePasswordAsync, payload.currentPassword, payload.newPassword);

    const response = yield call(updateProfileAsync, payload);

    Alert({
      type: 'success',
      title: `Success`,
      text: `The user was successfully edited.`,
    });

    yield put(Actions.updateProfileSuccess(response.data));
  } catch (error) {
    yield put(Actions.updateProfileFail(error.message));
    Alert({
      type: 'error',
      title: `Error`,
      text: `Something went wrong with the editing. Check your password and try again.`,
    });
  }
}

function* logout() {
  try {
    yield localStorage.removeItem('token');
    yield localStorage.removeItem('refresh');
  } catch (error) {
    // Logout intentionally continues even if storage clear fails:
    // the redux reset (RESET_STORE → user/tokens reset) is what the
    // user actually depends on. The structured log here makes any
    // browser-specific storage issue (Safari private mode, quota,
    // disabled storage) visible during triage.
    console.error('[logout] storage clear failed', {
      message: error?.message,
      name: error?.name,
    });
  }
}

// Re-hydrates state.auth.user from the backend using the JWT in localStorage.
// Needed when:
//   - The user opens the app via a deep-link (e.g. /profile) on a fresh tab and
//     redux-persist hasn't fully restored state yet.
//   - state.auth.user got cleared by something other than RESET_STORE.
//   - The persisted user shape is stale after a backend field rename.
function* fetchCurrentUser() {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      yield put(Actions.fetchCurrentUserFail());
      return;
    }

    const decoded = jwtDecode(token);
    // The backend JWT uses `sub` as the user id (standard JWT claim).
    const userId = decoded?.sub;
    if (!userId) {
      yield put(Actions.fetchCurrentUserFail());
      return;
    }

    const response = yield call(requestUser, userId);
    yield put(Actions.fetchCurrentUserSuccess(response.data));
  } catch (error) {
    console.error('[fetchCurrentUser] failed', {
      status: error?.response?.status,
      data: error?.response?.data,
      message: error?.message,
    });
    yield put(Actions.fetchCurrentUserFail());
    // Silent failure on purpose: this is a background refresh; the caller
    // (Profile page) can still show whatever data was persisted, and the
    // refresh-token interceptor will redirect to /signin if the token is
    // truly invalid.
  }
}

function* authWatcher() {
  yield takeLatest(Types.LOGIN, login);
  yield takeLatest(Types.UPDATE_PROFILE_REQUEST, updateProfile);
  yield takeLatest(Types.FETCH_CURRENT_USER, fetchCurrentUser);
  yield takeLatest(Types.RESET_STORE, logout);
}

export default function* authSaga() {
  return yield all([fork(authWatcher)]);
}
